/**
 * The exercises.
 *
 * For the weeks at his mother's place, where Elmar has no PC. Everything here
 * can be operated with the thumb: tap, next. No typing, because the on-screen
 * keyboard corrects automatically and would therefore do exactly what is
 * supposed to be practised.
 *
 * Three forms, depending on the mistake — see `exercise/exercises.ts`. What
 * they share: after every answer the correct solution is shown, even when the
 * guess was wrong. An exercise that only says "sorry, no" teaches nothing.
 */

import { useSignal, useComputed } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import * as st from '../state.js'
import * as store from '../editor/store.js'
import { PageBack } from './PageBack.js'
import { Icon, type IconName } from './icons.js'
import {
  exerciseFor,
  allIssues,
  weave,
  spread,
  askEveryStrategy,
  setWordCheck,
  type Exercise as ExerciseData,
  type Issue,
  type Token,
} from '../exercise/exercises.js'
import { addPractice, enoughForToday, DAILY_GOAL } from '../exercise/time.js'
import { stockIssues, solvedStock, markStockSolved } from '../exercise/stock.js'
import { addAnswer } from '../exercise/answers.js'

export function Exercise({ onSync }: { onSync: () => void }) {
  /** The issues of this round, fixed at the start. */
  const round = useSignal<Issue[]>([])
  /** All open ones — for the siblings in the same sentence. */
  const allOpen = useSignal<Issue[]>([])
  const index = useSignal(0)
  /** What was tapped — null as long as the exercise is open. */
  const answer = useSignal<string | null>(null)
  /** For upper/lower case: which words were toggled. */
  const toggled = useSignal<ReadonlySet<number>>(new Set())
  const solved = useSignal(0)
  const loading = useSignal(true)
  /**
   * Is it enough for today?
   *
   * Set when the measure is full, and once at the start — whoever has already
   * practised today is told so before the first exercise, and can carry on
   * anyway.
   */
  const done = useSignal(false)
  /** When the current exercise appeared — for the measure. */
  const shownAt = useSignal(Date.now())

  useEffect(() => {
    // The dictionary for „welches Wort gibt es nicht?" — the local speller
    // that stands behind LanguageTool anyway. Without it that form stays
    // out; the logic has no browser and cannot fetch one itself.
    const dict = st.checker?.fallback ?? null
    setWordCheck(dict ? (w) => dict.correct(w) : null)
    void (async () => {
      const all = await collect()
      allOpen.value = all
      // What gets solved along with an exercise must not come up again as an
      // exercise of its own. That is why only the first upper/lower case
      // issue per sentence goes into the round; the others hang off it.
      const seen = new Set<string>()
      const kept = all.filter((f) => {
        if (f.kind !== 'gross-klein' || !f.sentence) return true
        if (seen.has(f.sentence)) return false
        seen.add(f.sentence)
        return true
      })
      // His own spread out by variant, the stock woven in every fourth
      // place — not behind everything, where it never came up.
      const isStock = (f: Issue) => f.id.startsWith('stock-')
      // And every strategy the round holds gets asked at least once — the
      // counter alone left Zerlegen and Ableiten out for a whole round.
      round.value = askEveryStrategy(
        weave(spread(kept.filter((f) => !isStock(f))), kept.filter(isStock))
      )
      // No cut after ten any more: the round holds everything that is open,
      // and the measure of time decides when it is enough. Whoever has
      // practised today is told so straight away.
      done.value = enoughForToday()
      loading.value = false
      shownAt.value = Date.now()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exercise = useComputed<ExerciseData | null>(() => {
    const f = round.value[index.value]
    if (!f) return null
    // Pass along the other issues of the same sentence: with upper and lower
    // case they belong in the same exercise.
    const siblings = f.sentence
      ? allOpen.value.filter((g) => g.sentence === f.sentence)
      : []
    return exerciseFor(f, index.value, siblings)
  })

  const close = (): void => {
    st.exercise.value = false
    void collect().then((f) => (st.openIssues.value = f.length))
  }

  /** Remembers the solution and moves on. */
  const next = async (correct: boolean): Promise<void> => {
    const a = exercise.value
    // A parent trying the exercises out leaves no trace: no answer booked,
    // nothing marked solved, no minute on the day. The round moves on all
    // the same, and the same exercise is there again next time.
    const parent = st.isParent()
    if (a && !parent) {
      // Book the answer — the wrong ones too, and that is the point of it.
      // What the two stores below remember is only THAT something was
      // mastered in the end; how securely a kind of mistake sits cannot be
      // read from that. See `exercise/answers.ts`.
      //
      // The first issue names the exercise: with upper and lower case several
      // hang on one sentence, and they are one question with one answer.
      //
      // `f.kind` is already the kind the SENTENCE practises — `allIssues`
      // puts `sentenceKind` there where the model chose another word.
      const f = a.issues[0]
      if (f) addAnswer(f.id, f.kind, correct)
    }
    if (a && correct) {
      // All issues of the exercise, not just the first one: whoever got the
      // whole sentence right has dealt with all of them.
      if (!parent) for (const f of a.issues) await markSolved(f).catch(() => {})
      solved.value += 1
    }
    // Book the time of THIS exercise, then ask whether it is enough. Booking
    // first, because the last exercise counts too.
    const enough = parent ? false : addPractice(shownAt.value) >= DAILY_GOAL
    answer.value = null
    toggled.value = new Set()
    index.value += 1
    shownAt.value = Date.now()
    if (enough) done.value = true
    // Upload after every exercise: whoever practises on the phone should see
    // it on the PC. A failure is harmless, the regular cycle catches up.
    onSync()
  }

  /** Carry on although the measure is full. */
  const carryOn = (): void => {
    done.value = false
    shownAt.value = Date.now()
  }

  if (loading.value) {
    return <Shell onClose={close}><p class="ex-empty">Einen Augenblick …</p></Shell>
  }

  if (!round.value.length) {
    return (
      <Shell onClose={close}>
        <p class="ex-empty">
          Gerade gibt es nichts zu üben.
          <br />
          <small>Fotografiere eine Seite aus der Schule, dann kommen neue Wörter dazu.</small>
        </p>
      </Shell>
    )
  }

  // Enough for today — or nothing left over.
  //
  // Two endings that read differently: whoever has practised his time gets
  // „das reicht für heute" and may carry on. Whoever has worked through
  // everything gets told that there is nothing left, and carrying on would
  // lead nowhere.
  const empty = !exercise.value
  if (done.value || empty) {
    return (
      // The end of a sitting is a good outcome, so the ground says so.
      <Shell onClose={close} verdict="good">
        <div class="ex-done">
          <Icon name="sparkles" size={40} />
          <p class="ex-done-count">{empty ? 'Alles geübt.' : 'Das reicht für heute.'}</p>
          <p class="ex-done-text">
            {empty
              ? 'Fotografiere eine Seite aus der Schule, dann kommen neue Wörter dazu.'
              : praise(solved.value, index.value)}
          </p>
          <button class="action primary" onClick={close}>Fertig</button>
          {/* Only when there is really something left to do. */}
          {!empty && (
            <button class="ex-more" onClick={carryOn}>Trotzdem weiterüben</button>
          )}
        </div>
      </Shell>
    )
  }

  const a = exercise.value

  // A right answer turns the ground green. A wrong one leaves it alone.
  //
  // The red ground was tried and thrown out: it said "wrong" in the largest
  // voice the screen has, while the correct spelling sat in the middle of it
  // in green. What has to be taken away from a mistake is how the word IS
  // written — the struck-through choice already says which one was not it.
  const verdict = answer.value !== null && isCorrect(a, answer.value, toggled.value)
    ? 'good' as const
    : undefined

  return (
    <Shell onClose={close} verdict={verdict}>
      {/* No bar any more.

          It used to show the share of a round of ten. With the measure of
          time there is nothing to fill: a bar would be the clock that this is
          expressly not meant to have — whoever sees one practises against it
          and stops thinking at ninety percent. It ends when it ends. */}
      {/* The parent's reminder, above everything: what happens on this
          screen is a look, not his practising. */}
      {st.isParent() && <p class="ex-parent">Zählt nicht für {st.user.value?.name || 'das Kind'}.</p>}
      <Kind a={a} />
      <p class="ex-question">{question(a)}</p>

      {a.kind === 'schreibweise' && (
        <ChoiceExercise
          a={a}
          answer={answer.value}
          onChoice={(w) => (answer.value = w)}
        />
      )}

      {a.kind === 'finden' && (
        <FindExercise
          tokens={a.tokens ?? []}
          answer={answer.value}
          onTap={(w) => (answer.value = w.text)}
        />
      )}

      {a.kind === 'gross-klein' && (
        <CaseExercise
          a={a}
          toggled={toggled.value}
          done={answer.value !== null}
          onTap={(i) => {
            const nextSet = new Set(toggled.value)
            if (nextSet.has(i)) nextSet.delete(i)
            else nextSet.add(i)
            toggled.value = nextSet
          }}
        />
      )}

      {a.kind === 'stimmt' && (
        <TrueFalseExercise
          a={a}
          answer={answer.value}
          onChoice={(w) => (answer.value = w)}
        />
      )}

      {a.kind === 'ergaenzen' && (
        <CompleteExercise
          a={a}
          answer={answer.value}
          onChoice={(w) => (answer.value = a.stem + w)}
        />
      )}

      {a.kind === 'strategie' && (
        <StrategyExercise
          a={a}
          answer={answer.value}
          onChoice={(w) => (answer.value = w)}
        />
      )}

      {a.kind === 'gibtsnicht' && (
        <NonWordExercise
          a={a}
          answer={answer.value}
          onChoice={(w) => (answer.value = w)}
        />
      )}

      <div class="ex-foot">
        {answer.value === null ? (
          <>
            {/* Upper and lower case is the only form that needs a button of
                its own: nothing may be tapped there at all, and without it
                there would be no way of saying "I am done". All the others
                judge on the tap. */}
            {a.kind === 'gross-klein' && (
              <button
                class="action primary"
                onClick={() => (answer.value = checkCase(a, toggled.value) ? a.issue.sentenceRight : '')}
              >
                Fertig
              </button>
            )}

          </>
        ) : (
          <>
            {/* The line only speaks when it has something left to say.
             *
             * With „wie schreibt man das?" the right spelling now stands in
             * the sentence AND on its button, both marked. A third
             * „so wird es geschrieben: fahre" underneath says the same word a
             * third time and pulls the eye down away from the sentence, which
             * is the thing worth reading. */}
            {says(a) && (
              <p class={`ex-feedback${isCorrect(a, answer.value, toggled.value) ? ' gut' : ''}`}>
                {isCorrect(a, answer.value, toggled.value)
                  ? <Praise a={a} />
                  : a.kind === 'stimmt' && a.isRight
                    // Nothing to name. The word that would stand here is the
                    // one the sentence was built around — and under a sentence
                    // with no mistake in it, it read as the mistake.
                    ? <>Der Satz war richtig geschrieben. Es ist kein Fehler drin.</>
                    : a.kind === 'strategie'
                      // The name alone says which button it was; the tip
                      // says why. Both, or the child learns the label and
                      // not the move.
                      ? <>Hier hilft: <b>{a.issue.strategy}</b>{a.issue.tip && <>. {a.issue.tip}</>}</>
                      : <>{feedbackLead(a)}<b>{solution(a, toggled.value)}</b></>}
              </p>
            )}
            {/* The strategy, named, and the tip that applies it to the word.
             *
             * This is what school asks for and what the app never said: not
             * only HOW the word is written but by which way one gets there.
             * Only under a wrong answer. It stood under every answer at
             * first, so that whoever guessed right got the way too — but
             * under „Richtig." a line that explains why Weg is a noun reads
             * as if something still needed explaining, and the child looks
             * for the mistake in a sentence he just got right. Not under the
             * strategy exercise itself: there the tip IS the feedback. */}
            {a.kind !== 'strategie' && a.issue.tip &&
              !isCorrect(a, answer.value, toggled.value) && tipApplies(a, toggled.value) && (
              <p class="ex-tip">
                {a.issue.strategy && <b>{a.issue.strategy}:</b>} {a.issue.tip}
              </p>
            )}
            <button
              class="action primary"
              onClick={() => void next(isCorrect(a, answer.value, toggled.value))}
            >
              Weiter
            </button>
          </>
        )}
      </div>
    </Shell>
  )
}

/** The frame: the way back, the content below it. */
function Shell({
  onClose, children, verdict,
}: {
  onClose: () => void
  children: preact.ComponentChildren
  /**
   * A right answer turns the whole ground green.
   *
   * Only that one: a wrong answer leaves the ground as it was. A screen that
   * turns green is legible at arm's length, which is where a child holds a
   * phone when it is not sure — but a screen that turns red shouts about the
   * mistake while the solution stands quietly in the middle of it.
   */
  verdict?: 'good'
}) {
  return (
    <div class={`ex${verdict ? ' ' + verdict : ''}`} aria-label="Üben">
      {/* The same way back as everywhere else. This used to be a ✕ on the
          other side, because the screen was built as a dialog — which made
          the exercise the one place where leaving worked differently. */}
      <PageBack onBack={onClose} />
      <div class="ex-body">{children}</div>
    </div>
  )
}

/**
 * What kind of exercise this is — a symbol and two words, above the question.
 *
 * The question alone was not enough. „Stimmt dieser Satz?" over „Die Erde
 * dreht sich um die Sonne" reads as a question about the CONTENT, and Elmar
 * answered it as one: the first wrong sentence he got, he called right. Every
 * exercise here is about spelling, and five kinds look alike at a glance —
 * the symbol says which one it is before the sentence is read.
 */
function Kind({ a }: { a: ExerciseData }) {
  const [icon, label] = KINDS[a.kind]
  return (
    <p class="ex-kind">
      <Icon name={icon} size={24} />
      {label}
    </p>
  )
}

const KINDS: Record<ExerciseData['kind'], [IconName, string]> = {
  'finden': ['search', 'Fehler finden'],
  'gross-klein': ['case-sensitive', 'Groß oder klein'],
  'schreibweise': ['spell-check', 'Schreibweise wählen'],
  'stimmt': ['scale', 'Richtig geschrieben?'],
  'ergaenzen': ['puzzle', 'Wort ergänzen'],
  'strategie': ['lightbulb', 'Strategie'],
  'gibtsnicht': ['ban', 'Gibt es nicht'],
}

/**
 * „Welche Strategie hilft?" — the word, written right, and four names.
 *
 * The one form in which the word is shown CORRECTLY from the start. The
 * question is not how it is written but how one finds out — Schwingen,
 * Verlängern, Ableiten, Zerlegen, Merken. His class test asked exactly this,
 * with a sentence of explanation to write; here the explanation is the
 * feedback, and it is the model's tip for this very word.
 */
function StrategyExercise({
  a, answer, onChoice,
}: {
  a: ExerciseData
  answer: string | null
  onChoice: (w: string) => void
}) {
  return (
    <>
      <p class="ex-built">{a.issue.sentenceRight}</p>
      <div class="ex-choices">
        {(a.options ?? []).map((o) => (
          <button
            key={o}
            class={`ex-choice small${classFor(o, a.issue.strategy, answer)}`}
            disabled={answer !== null}
            onClick={() => onChoice(o)}
          >
            {o}
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * „Welches Wort gibt es nicht?" — three spellings, two of them words.
 *
 * The inverse of „wie schreibt man das?", and from the class test: reisen,
 * reißen, reissen. Two are words that mean different things; one is nothing.
 * Marking the non-word takes the rule and the vocabulary both.
 *
 * After the answer the non-word is the one marked „correct" — it IS the
 * right answer here, so the green goes to it. The two real words are dimmed
 * as spent, not struck through: they exist, and a line through a real word
 * would say the opposite.
 */
function NonWordExercise({
  a, answer, onChoice,
}: {
  a: ExerciseData
  answer: string | null
  onChoice: (w: string) => void
}) {
  return (
    <div class="ex-choices">
      {(a.options ?? []).map((o) => (
        <button
          key={o}
          class={`ex-choice${classFor(o, a.nonWord ?? '', answer)}`}
          disabled={answer !== null}
          onClick={() => onChoice(o)}
        >
          {o}
        </button>
      ))}
    </div>
  )
}

/** "Wie schreibt man das?" — two or three buttons. */
function ChoiceExercise({
  a, answer, onChoice,
}: {
  a: ExerciseData
  answer: string | null
  onChoice: (w: string) => void
}) {
  // In the sentence there is a gap at that position, not the wrong word.
  //
  // "Ich fare mit dem Rad" above two buttons, one of which reads "fare",
  // answers the question before it is asked: one looks for the spelling shown
  // above. The gap leaves the context in place — one sees that it is about
  // riding a bike — and does not give the answer away.
  //
  // The gap is a RULE, not three underscore characters. Those took the colour
  // of the text and read as part of the sentence — a white stroke among white
  // words. A blue line says "something is missing here" without pretending to
  // be a word.
  const gapAt = a.issue.sentence ? a.issue.sentence.indexOf(a.issue.sentenceWrong) : -1

  // After the answer the gap is filled — with the RIGHT spelling, marked
  // green.
  //
  // Never with the red squiggle, whatever he chose. What stands in the gap is
  // the correct form, and the red wave means "there is a mistake here"
  // everywhere else in this app: a red line under a correctly spelled
  // "Hausaufgaben" teaches the opposite of the exercise. The red wave is for
  // „stimmt dieser Satz?", where a wrong word really does stand in the
  // sentence.
  //
  // The gap has done its job by then; leaving it open means the sentence he
  // was working on never gets finished.
  const done = answer !== null

  return (
    <>
      {gapAt >= 0 && (
        <p class="ex-context">
          {a.issue.sentence.slice(0, gapAt)}
          {done
            ? <span class="ex-fixed">{a.issue.sentenceRight}</span>
            : (
              // As wide as the word that belongs there, so the sentence does
              // not re-wrap when it is filled in.
              <span class="ex-gapline" aria-label="fehlendes Wort">
                <span class="ex-spacer" aria-hidden="true">{a.issue.sentenceRight}</span>
              </span>
            )}
          {a.issue.sentence.slice(gapAt + a.issue.sentenceWrong.length)}
        </p>
      )}
      <div class="ex-choices">
        {(a.options ?? []).map((o) => (
          <button
            key={o}
            class={`ex-choice${classFor(o, a.issue.sentenceRight, answer)}`}
            disabled={answer !== null}
            onClick={() => onChoice(o)}
          >
            {o}
          </button>
        ))}
      </div>
    </>
  )
}

/** "Welches Wort ist falsch?" — the sentence, tappable word by word. */
function FindExercise({
  tokens, answer, onTap,
}: {
  tokens: Token[]
  answer: string | null
  onTap: (w: Token) => void
}) {
  return (
    <p class="ex-sentence">
      {tokens.map((w) => (
        <span key={w.i}>
          <button
            class={
              'ex-token' +
              (answer === null ? '' : w.isTarget ? ' correct' : answer === w.text ? ' wrong' : '')
            }
            disabled={answer !== null}
            onClick={() => onTap(w)}
          >
            {w.text}
          </button>
          <span class="ex-trailing">{w.trailing}</span>{' '}
        </span>
      ))}
    </p>
  )
}

/**
 * "Welche Wörter schreibt man groß?" — tapping toggles.
 *
 * Not a selection, but a real toggle: afterwards the word stands there the way
 * he would write it. Tapping again undoes it.
 */
function CaseExercise({
  a, toggled, done, onTap,
}: {
  a: ExerciseData
  toggled: ReadonlySet<number>
  done: boolean
  onTap: (i: number) => void
}) {
  return (
    <p class="ex-sentence">
      {(a.tokens ?? []).map((w) => {
        const isToggled = toggled.has(w.i)
        const shown = isToggled ? flipCase(w.text) : w.text
        // Only what he touched is judged — and what he should have touched.
        // Colouring all words green, including the twelve that were right
        // anyway, praises him for doing nothing and makes the one word that
        // mattered invisible.
        const mark = !done ? '' : isToggled ? (w.isTarget ? ' correct' : ' wrong') : w.isTarget ? ' missed' : ''
        return (
          <span key={w.i}>
            <button
              class={'ex-token' + (isToggled ? ' toggled' : '') + mark}
              disabled={done}
              onClick={() => onTap(w.i)}
            >
              {/*
                Both versions on top of each other: the wider one keeps the
                space free, the visible one lies above it. "Unser" is wider
                than "unser", and without this the sentence would re-wrap on
                tapping — the text would jump away under the finger.

                The width comes from the placeholder, because it sits in the
                flow; the visible word is positioned absolutely and centres
                itself within it.
              */}
              <span class="ex-spacer" aria-hidden="true">{widerForm(w.text)}</span>
              <span class="ex-shown">{shown}</span>
            </button>
            <span class="ex-trailing">{w.trailing}</span>{' '}
          </span>
        )
      })}
    </p>
  )
}

/**
 * "Stimmt dieser Satz?" — one sentence, two buttons.
 *
 * Half of the sentences are correct. That is the point of the form: every
 * other exercise here shows something wrong and asks what it is, and whoever
 * only ever sees wrong things learns to find a mistake even where there is
 * none. Leaving a correct sentence alone has to be practised too.
 */
function TrueFalseExercise({
  a, answer, onChoice,
}: {
  a: ExerciseData
  answer: string | null
  onChoice: (w: string) => void
}) {
  const shown = a.isRight
    ? a.issue.sentence.replace(a.issue.sentenceWrong, a.issue.sentenceRight)
    : a.issue.sentence

  // After the answer the wrong word gets the red squiggle — the same one the
  // editor draws while writing.
  //
  // The solution used to name the word underneath ("Der Fehler steckt in:
  // geschriben") while the sentence above stayed untouched: the child read
  // the word, then had to find it again up there. The line points straight at
  // it.
  //
  // Only AFTER the answer: a mark beforehand would answer the question.
  //
  // On a correct sentence the mark is a quiet one, and only there to give
  // the tip below something to point at. „Ersatzprobe: ... also denn, nicht
  // den" under a sentence with nothing marked read like a riddle — the child
  // had no way of knowing which word the exercise was built around. The tip
  // comes only after a wrong answer, so the mark does too: a dotted line
  // under „denn" after „Richtig. Es ist kein Fehler drin." made the child
  // look for the mistake he had just rightly said was not there.
  const word = a.isRight ? a.issue.sentenceRight : a.issue.sentenceWrong
  const marked = answer !== null &&
    (!a.isRight || (!!a.issue.tip && answer !== 'stimmt'))
  const at = marked ? shown.indexOf(word) : -1

  return (
    <>
      <p class="ex-context big">
        {at < 0 ? shown : (
          <>
            {shown.slice(0, at)}
            <span class={a.isRight ? 'ex-key' : 'ex-flag'}>{word}</span>
            {shown.slice(at + word.length)}
          </>
        )}
      </p>
      <div class="ex-choices">
        {(['stimmt', 'stimmt nicht'] as const).map((o) => (
          <button
            key={o}
            class={`ex-choice${classFor(o, a.isRight ? 'stimmt' : 'stimmt nicht', answer)}`}
            disabled={answer !== null}
            onClick={() => onChoice(o)}
          >
            {/* Named after the spelling, not after the truth: „Stimmt"
                was answered for what the sentence SAYS. */}
            {o === 'stimmt' ? 'Richtig geschrieben' : 'Fehler drin'}
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * "Wie geht das Wort weiter?" — the word opened at the place of the mistake.
 *
 * A jigsaw was tried first: all the parts shuffled, tap them into order. It
 * practised nothing — with two pieces there are two orders, and the wrong one
 * reads "lungStrah". Nobody needs a rule to rule that out.
 *
 * So the word is not scrambled but OPENED: the stem stands there, and the
 * ending is chosen. "Stra…" with "hlung" against "lung" is the silent h and
 * nothing else — the same question the mistake asked, without a sentence
 * around it to read past.
 */
function CompleteExercise({
  a, answer, onChoice,
}: {
  a: ExerciseData
  answer: string | null
  onChoice: (w: string) => void
}) {
  const stem = a.stem ?? ''

  return (
    <>
      {/* The stem with the gap behind it, so one sees WHERE it goes on. */}
      <p class="ex-built">
        {stem}
        <span class="ex-gap">…</span>
      </p>
      <div class="ex-choices">
        {(a.parts ?? []).map((o) => (
          <button
            key={o}
            class={`ex-choice${classFor(stem + o, a.issue.sentenceRight, answer)}`}
            disabled={answer !== null}
            onClick={() => onChoice(o)}
          >
            <span class="ex-dim">{stem}</span>{o}
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * Does the line under the exercise still have something to say?
 *
 * It was written when the exercise showed nothing but buttons. Since the
 * sentence carries the answer — the gap filled in green, the wrong word
 * marked — the line often only repeats what is already on the screen, in
 * smaller type and further from where the eye is.
 *
 * It stays silent where the exercise has already said it, and speaks where
 * it has not:
 *
 * - „wie schreibt man das?" — silent. The right spelling stands in the
 *   sentence and on its own button, both marked. A third mention adds
 *   nothing.
 * - „welches Wort ist falsch?" — speaks. Tapping the wrong word does not say
 *   how it is written instead.
 * - „stimmt dieser Satz?" — speaks. The sentence shows the mistake; the right
 *   spelling appears nowhere else.
 * - groß/klein — speaks. Which words were missed is not otherwise named.
 * - „wie geht das Wort weiter?" — speaks when wrong; the endings alone do not
 *   spell the word out.
 */
const says = (a: ExerciseData): boolean => a.kind !== 'schreibweise'

/** What stands in front of the solution when the answer was wrong. */
function feedbackLead(a: ExerciseData): string {
  if (a.kind === 'gross-klein') return 'Das stimmt noch nicht: '
  if (a.kind === 'gibtsnicht') return 'Dieses Wort gibt es nicht: '
  // With „stimmt" the wrong word is now marked in the sentence above, so
  // naming it again here would say the same thing twice. What is NOT up there
  // is how it should be written — that is what belongs here.
  if (a.kind === 'stimmt') return 'Richtig wäre: '
  return 'So wird es geschrieben: '
}

/**
 * The wider of the two versions — written with a capital or a small letter.
 *
 * Almost always the capitalised one, but not with every font and not with
 * every letter. Nothing is measured: the browser lays out both, and the wider
 * one wins by itself if you give it the space.
 */
function widerForm(w: string): string {
  const c = w[0]
  if (!c) return w
  const upper = c.toUpperCase() + w.slice(1)
  const lower = c.toLowerCase() + w.slice(1)
  // Without measuring, the capitalised form is the safe choice: a capital
  // letter is at least as wide as its small counterpart in practically every
  // font.
  return upper.length >= lower.length ? upper : lower
}

/** Flips the first letter. */
function flipCase(w: string): string {
  const c = w[0]
  if (!c) return w
  return c === c.toUpperCase() ? c.toLowerCase() + w.slice(1) : c.toUpperCase() + w.slice(1)
}

/**
 * The question above the exercise.
 *
 * With upper and lower case it must say WHAT it is about: "words that are
 * written differently" can mean anything — a missing letter, a lengthening h,
 * a mix-up. Whoever does not know what to look for cannot solve the exercise,
 * even if he knows the rule.
 *
 * The direction is stated as well, because it follows from the mistakes:
 * usually nouns are written in lower case, so it says "großschreiben". If both
 * come up, the general form stays.
 */
function question(a: ExerciseData): string {
  if (a.kind === 'schreibweise') return 'Wie schreibt man das?'
  if (a.kind === 'finden') return 'Welches Wort ist falsch geschrieben?'
  if (a.kind === 'stimmt') return 'Ist dieser Satz richtig geschrieben?'
  if (a.kind === 'ergaenzen') return 'Wie geht das Wort weiter?'
  if (a.kind === 'strategie') return 'Mit welcher Strategie findest du heraus, wie man dieses Wort schreibt?'
  if (a.kind === 'gibtsnicht') return 'Welches Wort gibt es nicht?'
  // The whole sentence is shown in lower case, so ALL words to be capitalised
  // are wanted — no longer only the one reported mistake.
  return 'Hier ist alles kleingeschrieben. Tippe die Wörter an, die großgeschrieben werden.'
}

/**
 * What stands there when the answer was right.
 *
 * With the correct spelling, not just "Richtig." — finding the mistake is not
 * the same as knowing how the word is written. Whoever taps „schwimt" has
 * seen that something is wrong there; whether he could write „schwimmt"
 * himself is another question, and the answer belongs here.
 *
 * Except for the capitalisation exercise: there the corrected sentence
 * already stands above, every word in its right form. Repeating it below
 * would say nothing new.
 */
function Praise({ a }: { a: ExerciseData }) {
  if (a.kind === 'gross-klein') return <>Richtig.</>
  // With „stimmt" the correct sentence already stands above, and with a
  // sentence that was right there is no word to name. Saying „es heißt:
  // fahre" under a sentence he rightly let stand would sound like a
  // correction.
  if (a.kind === 'stimmt' && a.isRight) return <>Richtig. Es ist kein Fehler drin.</>
  // The strategy was the question; the tip is the answer spelled out. The
  // word itself was never in doubt here.
  if (a.kind === 'strategie') {
    return <>Richtig. {a.issue.tip || <>Mit <b>{a.issue.strategy}</b> findest du es heraus.</>}</>
  }
  // The non-word is named as what it is, and the right spelling beside it:
  // knowing what does not exist is half the answer.
  if (a.kind === 'gibtsnicht') {
    return <>Richtig. <b>{a.nonWord}</b> gibt es nicht. Es heißt: <b>{a.issue.sentenceRight}</b></>
  }
  return (
    <>
      Richtig. Es heißt: <b>{a.issue.sentenceRight}</b>
    </>
  )
}

/** Is the answer correct? */
function isCorrect(a: ExerciseData, answer: string | null, toggled: ReadonlySet<number>): boolean {
  if (a.kind === 'gross-klein') return checkCase(a, toggled)
  if (a.kind === 'finden') return answer === a.issue.sentenceWrong
  // „stimmt" is answered with one of the two buttons, and which one is right
  // depends on which version of the sentence was shown.
  if (a.kind === 'stimmt') return answer === (a.isRight ? 'stimmt' : 'stimmt nicht')
  if (a.kind === 'strategie') return answer === a.issue.strategy
  if (a.kind === 'gibtsnicht') return answer === a.nonWord
  // Completing compares the whole word: the chosen ending is put behind the
  // stem before it gets here.
  return answer === a.issue.sentenceRight
}

/** For upper/lower case: exactly the target word toggled, no other one. */
function checkCase(a: ExerciseData, toggled: ReadonlySet<number>): boolean {
  const targets = (a.tokens ?? []).filter((w) => w.isTarget).map((w) => w.i)
  if (toggled.size !== targets.length) return false
  return targets.every((i) => toggled.has(i))
}

/**
 * The solution as it is shown below the exercise.
 *
 * With upper and lower case these are the words he MISSED or touched
 * wrongly — not the mistake that brought him here. "So wird es geschrieben:
 * Zoo" below a sentence in which he had Zoo right and missed `im` explains
 * nothing and praises the wrong thing.
 */
function solution(a: ExerciseData, toggled: ReadonlySet<number>): string {
  if (a.kind === 'strategie') return a.issue.strategy
  if (a.kind === 'gibtsnicht') return a.nonWord ?? ''
  if (a.kind !== 'gross-klein') {
    return a.issues.map((f) => f.sentenceRight).join(', ')
  }
  const wrongOnes = (a.tokens ?? []).filter((w) => w.isTarget !== toggled.has(w.i))
  // Show them capitalised the way they belong — even the ones he capitalised
  // wrongly are shown here in their correct form.
  return wrongOnes.map((w) => (w.isTarget ? flipCase(w.text) : w.text)).join(', ')
}

/**
 * Whether the tip speaks to what just went wrong.
 *
 * The tip belongs to the word the exercise was built around. With upper and
 * lower case the child may have had THAT word right and slipped on another
 * one — „Erstes" in „als Erstes", with the tip talking about „Spiel". Under
 * „Das stimmt noch nicht: Erstes" a line that explains why Spiel is a noun
 * reads as if the app had marked the wrong word. So the tip stays out
 * unless the key word is among the ones he got wrong.
 */
function tipApplies(a: ExerciseData, toggled: ReadonlySet<number>): boolean {
  if (a.kind !== 'gross-klein') return true
  const wrongOnes = (a.tokens ?? []).filter((w) => w.isTarget !== toggled.has(w.i))
  const key = a.issue.sentenceRight.toLowerCase()
  return wrongOnes.some((w) => w.text.toLowerCase() === key)
}

/** The colour of a button after the answer was given. */
function classFor(o: string, right: string, answer: string | null): string {
  if (answer === null) return ''
  if (o === right) return ' correct'
  // The one he tapped is struck through — that is his answer, named.
  if (o === answer) return ' wrong'
  // Every OTHER spelling is wrong too, and has to look it.
  //
  // It used to keep the plain face, and once the right answer turned green
  // that plain face was the brightest thing left among the options: „fare"
  // sat there looking as available as it had before the answer. Dimmed, it
  // reads as what it is — a spelling that was never right either.
  return ' spent'
}

/**
 * A sentence at the end.
 *
 * No grade and no percentage: it is about what he managed, and not about what
 * is missing.
 */
function praise(n: number, total: number): string {
  // Nothing done in this sitting — he had already practised his time today
  // and opened the app again. "Alles richtig" would praise him for no
  // exercises at all; 0 === 0 read as a perfect score.
  if (total === 0) return 'Heute hast du schon geübt.'
  if (n === total) return 'Alles richtig. Das saß.'
  if (n >= total * 0.7) return 'Das meiste sitzt schon.'
  if (n >= total * 0.4) return 'Wird schon besser. Morgen wieder?'
  return 'Schwierige Wörter. Die üben wir noch mal.'
}

/**
 * All open issues, from both sources.
 *
 * The photographed pages are in `mistakes`, the words corrected while writing
 * are in `words`. For the exercise these are the same thing: a word he wrote
 * wrongly.
 */
async function collect(): Promise<Issue[]> {
  const [m, w] = await Promise.all([
    store.mistakes().catch(() => []),
    store.corrections().catch(() => []),
  ])
  const solved = new Set(w.filter((x) => x.practisedAt != null).map((x) => `w-${x.key}`))
  const own = allIssues(m, w, solved)

  // The stock fills up behind his own mistakes.
  //
  // Never in front of them: a sentence he wrote wrongly himself is worth more
  // than any invented one. It only comes into play when his own are used up —
  // or on the first day, when there are none yet.
  //
  // Enough that a sitting does not run dry, not the whole catalogue: the round
  // is ended by the clock, and two hundred exercises in the list would only
  // slow the start.
  // At least a dozen, whatever he has of his own: they are what keeps a
  // sitting from being thirty rounds of his own two rules, and the rows for
  // his class are in there. Where they go in the round is `weave`'s job.
  const stock = stockIssues(own, solvedStock(), Math.max(12, 40 - own.length))
  return [...own, ...stock]
}

/**
 * Records that an issue was mastered in the exercise.
 *
 * Two sources, two stores: a find from a photo gets `solved`, a practised word
 * gets `practisedAt`. Both go through the sync, so the other device knows
 * about it as well.
 */
async function markSolved(f: Issue): Promise<void> {
  // Derived exercises carry a "+0" on their id — they belong to the same
  // mistake, on another word of the same rule. Solving one marks the mistake
  // as practised: the rule is what is being trained, and once it sits it sits.
  const base = f.id.replace(/\+\d+$/, '')

  // A stock exercise has no row in the database — it belongs to no mistake of
  // his. It is remembered on the device, so the catalogue moves on instead of
  // starting from the top every time.
  if (base.startsWith('stock-')) {
    markStockSolved(base)
    return
  }

  if (f.source === 'photo') {
    const all = await store.mistakes()
    const m = all.find((x) => x.id === base)
    if (m) await store.putMistake({ ...m, solved: Date.now() })
    return
  }
  const key = base.replace(/^w-/, '')
  const all = await store.corrections()
  const w = all.find((x) => x.key === key)
  if (w) await store.putWord({ ...w, practisedAt: Date.now() })
}
